import { useEffect, useRef } from 'react';
import './surfaces.css';

const openSurfaces = [];
const focusable = root => [...root.querySelectorAll('a[href], button, input, textarea, select, [tabindex]')]
  .filter(node => !node.disabled && node.tabIndex >= 0 && node.getClientRects().length && !node.closest('[inert]'));

// Inline catalog examples share appearance without taking focus from the page.
export function Surface({ kind = 'dialog', inline = false, width, height, direction = 'column', onClose, children, className = '', style, ...props }) {
  const root = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (inline) return;
    const node = root.current;
    const previous = document.activeElement;
    openSurfaces.push(node);
    const isTop = () => openSurfaces.at(-1) === node;
    const focusFirst = () => (focusable(node)[0] || node).focus();
    const frame = requestAnimationFrame(() => {
      if (isTop() && !node.contains(document.activeElement)) focusFirst();
    });
    const keydown = event => {
      if (!isTop()) return;
      if (event.key === 'Escape' && close.current) {
        event.preventDefault();
        event.stopImmediatePropagation();
        close.current();
      }
      if (event.key !== 'Tab') return;
      const items = focusable(node);
      const first = items[0], last = items.at(-1);
      if (!items.length || !node.contains(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last || node : first || node).focus();
      }
    };
    const focusin = event => { if (isTop() && !node.contains(event.target)) focusFirst(); };
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('focusin', focusin);
    return () => {
      cancelAnimationFrame(frame);
      const wasTop = isTop();
      openSurfaces.splice(openSurfaces.indexOf(node), 1);
      document.removeEventListener('keydown', keydown, true);
      document.removeEventListener('focusin', focusin);
      if (wasTop && previous?.isConnected) previous.focus();
    };
  }, [inline]);
  return <section {...props} ref={root} role={inline ? 'region' : 'dialog'} aria-modal={inline ? undefined : true} tabIndex={-1} data-ds="Surface" data-kind={kind} data-direction={direction} data-inline={inline || undefined} className={`ds-surface ${className}`} style={{ ...style, '--surface-width': width, '--surface-height': height }}>{children}</section>;
}

export function Scrim({ onClose, layer = 80 }) {
  return <div data-ds="Scrim" className="ds-scrim" aria-hidden="true" onClick={onClose} style={{ zIndex: layer }} />;
}

export function MenuSurface({ inline = false, onClose, children, style, ...props }) {
  const root = useRef(null);
  const restore = useRef(true);
  useEffect(() => {
    if (inline) return;
    const previous = document.activeElement;
    const node = root.current;
    (node.querySelector('[aria-pressed="true"]') || focusable(node)[0] || node).focus();
    const outside = event => { if (!node.contains(event.target) && !previous?.contains(event.target)) onClose?.(); };
    document.addEventListener('pointerdown', outside);
    return () => { document.removeEventListener('pointerdown', outside); if (restore.current && previous?.isConnected) previous.focus(); };
  }, [inline]);
  const placement = Object.fromEntries(Object.entries(style || {}).filter(([key]) => ['left', 'right', 'top', 'bottom', 'zIndex', 'width', 'minWidth', 'maxWidth'].includes(key)));
  return <div {...props} ref={root} data-ds="MenuSurface" data-inline={inline || undefined} className="ds-menu-surface" role="group" aria-label={props['aria-label'] || 'Options'} tabIndex={-1} style={placement} onBlur={event => {
    if (!inline && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) { restore.current = false; onClose?.(); }
  }} onKeyDown={event => {
    if (event.key === 'Escape') { event.stopPropagation(); event.preventDefault(); onClose?.(); }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const items = focusable(root.current);
    if (!items.length) return;
    const index = items.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    event.preventDefault(); items[next].focus();
  }}>{children}</div>;
}
